import type { Bet365SelectionData, BetMGMSelectionData, DraftKingsSelectionData, FanDuelSelectionData, PolymarketSelectionData, UnderdogSelectionData } from '../lib/api-client'
import type { Sportsbook } from '@/types/types'
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useEffect, useRef, useState } from 'react'
import { useEvents } from '../hooks/useEvents'
import { trackMarketChange, trackOddsClick, trackWidgetView } from '../lib/analytics'
import { globalBetSlipManager } from './GlobalBetSlipManager'
import { useUIPreferences } from './UIPreferencesManager'
import Widget, { H2H_SENTINEL } from './Widget'

// Transformed selection data types (for bet slip - all strings)
type TransformedSelectionData
  = | { marketId: string, selectionId: string } // FanDuel
    | { selectionId: string } // DraftKings/Bet365
    | { optionId: string, marketId: string, fixtureId: string } // BetMGM
    | { marketId: string, marketSlug: string, eventId: string, eventSlug: string } // Polymarket
    | { optionId: string, lineId: string, matchKey: string } // Underdog

// Over/under selections share a player name; the bet slip and analytics
// need the side and line spelled out ("Gable Steveson Over 12.5").
export function selectionLabel(selection: { name: string, side?: 'over' | 'under', line?: string }): string {
  if (!selection.side)
    return selection.name
  return `${selection.name} ${selection.side === 'over' ? 'Over' : 'Under'} ${selection.line ?? ''}`.trim()
}

interface AppProps {
  instanceId: string
  league: string
  /**
   * How the widget renders when the league has no odds:
   * 'message' (default) shows the static no-odds card, 'hidden' renders
   * nothing at all.
   */
  emptyState?: 'message' | 'hidden'
  /**
   * The shadow host element (standalone widget mode only). Lets the widget
   * honor the embed contract's `data-asd-hide-when-empty` ancestor opt-in
   * by hiding/restoring the host's own wrapper section.
   */
  hostElement?: HTMLElement
  /**
   * Sub-affiliate embed flag — forwarded as ?aff= so the bet slip deep-links
   * open the sub-affiliate's own per-book URLs; books they have no link for
   * get a no-op button instead of our house link.
   */
  subAffiliate?: string
}

export function App({
  instanceId,
  league,
  emptyState = 'message',
  hostElement,
  subAffiliate,
}: AppProps) {
  const { data: response, isPending, error } = useEvents(league, subAffiliate)
  const { oddsFormat, toggleOddsFormat } = useUIPreferences()
  const [selectedMarketIndex, setSelectedMarketIndex] = useState<number>(0)
  const hasTrackedView = useRef(false)

  // Embed contract: an ancestor of the mount element may carry
  // `data-asd-hide-when-empty`, meaning "hide this whole wrapper (e.g. a
  // section whose heading would otherwise float over nothing) when the
  // sport has no odds". The WP loader shim already honors it for unknown
  // slugs at mount time; this covers known slugs whose odds feed is empty
  // right now. Symmetric on purpose — if odds appear on a later poll, the
  // wrapper (and any in-page nav link pointing at it) is restored.
  const isEmpty = !isPending && !error && (!response?.data || response.data.length === 0)
  useEffect(() => {
    if (!hostElement)
      return
    const wrap = hostElement.closest<HTMLElement>('[data-asd-hide-when-empty]')
    if (!wrap)
      return
    const display = isEmpty ? 'none' : ''
    wrap.style.display = display
    if (wrap.id) {
      document.querySelectorAll<HTMLElement>(`a[href="#${wrap.id}"]`)
        .forEach((a) => { a.style.display = display })
    }
  }, [hostElement, isEmpty])

  const handleMarketChange = (marketIndex: number) => {
    setSelectedMarketIndex(marketIndex)
    const market = response?.data?.[marketIndex]
    if (market) {
      trackMarketChange(league, market.marketName, market.name)
    }
  }

  if (isPending) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-10 h-10 border-4 border-[#C8E2FF] border-t-[#0db5ff] rounded-full animate-spin dark:border-(--ow-hairline) dark:border-t-(--ow-accent)" />
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-red-500 p-5 dark:text-red-400">
        Error:
        {error.message}
      </div>
    )
  }

  if (!response || !response.data || response.data.length === 0) {
    if (emptyState === 'hidden')
      return null
    const leagueName = response?.leagueName ?? league
    return (
      <div
        className="ow-empty-state @container bg-white rounded-lg shadow-xl overflow-hidden mx-auto border-[3px] border-[#C8E2FF] py-6 px-6 dark:bg-(--ow-surface) dark:border-(--ow-border) dark:shadow-[0_4px_14px_rgba(0,0,0,0.55)]"
        style={{ maxWidth: '1440px' }}
      >
        <div className="text-center max-w-md mx-auto">
          <div className="text-xl font-bold text-gray-700 dark:text-(--ow-ink)">
            <div>There are currently no odds available for</div>
            <div>{leagueName}</div>
          </div>
          <div className="text-sm text-gray-500 mt-3 dark:text-(--ow-meta)">
            Check back soon — as soon as odds are posted, they'll appear here.
          </div>
        </div>
      </div>
    )
  }

  // Register sportsbook brand metadata for this league so the widget can
  // render brand-colored buttons + logos and the bet slip can deep-link.
  if (response.sportsbooks) {
    globalBetSlipManager.setSportsbookMeta(league, response.sportsbooks)
  }

  const events = response.data
  const h2hStartIndex = response.h2hStartIndex
  const h2hEndIndex = response.h2hEndIndex

  // Build H2H indices from the backend-provided start/end range
  const h2hIndices = h2hStartIndex != null
    ? events.map((_, i) => i).filter(i => i >= h2hStartIndex && (h2hEndIndex == null || i < h2hEndIndex))
    : []

  // Pure H2H mode only when the ENTIRE payload is matchups (PFL/BKFC).
  // A sport can lead with H2H and still carry list markets after the range
  // (PBR: five bouts then Team Series Championship) — that renders hybrid,
  // with the matchups grouped under the Head to Head dropdown option.
  const isH2HMode = h2hIndices.length > 0 && h2hIndices.length === events.length
  const hasH2H = h2hIndices.length > 0

  if (!hasTrackedView.current) {
    hasTrackedView.current = true
    trackWidgetView(league)
  }

  const handleSelectionClick = (marketIndex: number, selectionIndex: number, sportsbook: Sportsbook) => {
    const event = events[marketIndex]
    if (!event?.selections?.length || !instanceId) {
      console.warn('Missing required data for bet selection')
      return
    }

    const selection = event.selections[selectionIndex]
    if (!selection) {
      console.warn(`Selection at index ${selectionIndex} not found`)
      return
    }

    const oddsData = selection.odds[sportsbook]
    if (!oddsData) {
      console.warn(`No odds data found for ${sportsbook}`)
      return
    }

    // Convert selectedData to ensure string types for compatibility
    let selectedData: TransformedSelectionData

    // Handle different sportsbook data structures
    if (sportsbook === 'FanDuel' && oddsData.selectionData) {
      const fanDuelData = oddsData.selectionData as FanDuelSelectionData
      selectedData = {
        marketId: fanDuelData.marketId,
        selectionId: fanDuelData.selectionId.toString(),
      }
    }
    else if (sportsbook === 'DraftKings' && oddsData.selectionData) {
      const draftKingsData = oddsData.selectionData as DraftKingsSelectionData
      selectedData = {
        selectionId: draftKingsData.selectionId.toString(),
      }
    }
    else if (sportsbook === 'Bet365' && oddsData.selectionData) {
      const bet365Data = oddsData.selectionData as Bet365SelectionData
      selectedData = {
        selectionId: bet365Data.selectionId.toString(),
      }
    }
    else if (sportsbook === 'BetMGM' && oddsData.selectionData) {
      const betMGMData = oddsData.selectionData as BetMGMSelectionData
      selectedData = {
        optionId: betMGMData.optionId.toString(),
        marketId: betMGMData.marketId.toString(),
        fixtureId: betMGMData.fixtureId.toString(),
      }
    }
    else if (sportsbook === 'Polymarket' && oddsData.selectionData) {
      const polymarketData = oddsData.selectionData as PolymarketSelectionData
      selectedData = {
        marketId: polymarketData.marketId,
        marketSlug: polymarketData.marketSlug,
        eventId: polymarketData.eventId,
        eventSlug: polymarketData.eventSlug,
      }
    }
    else if (sportsbook === 'Underdog' && oddsData.selectionData) {
      const underdogData = oddsData.selectionData as UnderdogSelectionData
      selectedData = {
        optionId: underdogData.optionId,
        lineId: underdogData.lineId,
        matchKey: underdogData.matchKey,
      }
    }
    else {
      // Default empty object if no selectionData available
      selectedData = { selectionId: '' }
    }

    const betData = {
      league,
      eventName: event.name,
      marketName: event.marketName,
      sportsbook,
      selectionName: selectionLabel(selection),
      odds: {
        american: oddsData.american,
        decimal: oddsData.decimal,
      },
      selectedData,
      stake: '',
    }

    trackOddsClick(league, sportsbook, selectionLabel(selection), event.marketName, event.name)
    globalBetSlipManager.addBet(instanceId, betData)
  }

  // In hybrid mode individual H2H entries aren't dropdown options — they're
  // grouped under the Head to Head sentinel. When matchups lead the payload
  // the initial index 0 lands inside the H2H range; snap it to the group.
  const displayMarketIndex = !isH2HMode && h2hIndices.includes(selectedMarketIndex)
    ? H2H_SENTINEL
    : selectedMarketIndex

  return (
    <Widget
      league={league}
      leagueName={response.leagueName}
      sportsbookMeta={response.sportsbooks}
      markets={events}
      selectedMarketIndex={displayMarketIndex}
      onMarketChange={handleMarketChange}
      oddsFormat={oddsFormat}
      onToggleOddsFormat={toggleOddsFormat}
      onSelectionClick={handleSelectionClick}
      isH2HMode={isH2HMode}
      h2hIndices={hasH2H ? h2hIndices : undefined}
      eventName={h2hStartIndex != null && h2hStartIndex > 0 ? events[0]!.name : undefined}
    />
  )
}

const queryClient = new QueryClient()

export default function AppWidget(props: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <App {...props} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
